import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useSundaySchool } from '@/hooks/useSundaySchool';
import { SundaySchoolLessonFormValues } from '@/types/sundaySchoolTypes';

const formSchema = z.object({
  class_id: z.string().min(1, 'Selecione uma turma'),
  teacher_id: z.string().min(1, 'Selecione um professor'),
  lesson_date: z.date({
    required_error: 'Data da aula é obrigatória',
  }),
  topic: z.string().optional(),
  offering_pix: z.string().optional(),
  offering_cash: z.string().optional(),
  notes: z.string().optional(),
});

interface SundaySchoolLessonFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lesson?: any;
}

export const SundaySchoolLessonForm: React.FC<SundaySchoolLessonFormProps> = ({
  open,
  onOpenChange,
  lesson,
}) => {
  const { createLesson, updateLesson, classes, teachers, loading } = useSundaySchool();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      class_id: '',
      teacher_id: '',
      lesson_date: '' as any,
      topic: '',
      offering_pix: '',
      offering_cash: '',
      notes: '',
    },
  });

  React.useEffect(() => {
    if (open && lesson) {
      form.reset({
        class_id: lesson.class_id || '',
        teacher_id: lesson.teacher_id || '',
        lesson_date: lesson.lesson_date ? new Date(lesson.lesson_date + 'T00:00:00') : ('' as any),
        topic: lesson.topic || '',
        offering_pix: lesson.offering_pix != null ? String(lesson.offering_pix) : '',
        offering_cash: lesson.offering_cash != null ? String(lesson.offering_cash) : '',
        notes: lesson.notes || '',
      });
    } else if (open && !lesson) {
      form.reset({
        class_id: '',
        teacher_id: '',
        lesson_date: '' as any,
        topic: '',
        offering_pix: '',
        offering_cash: '',
        notes: '',
      });
    }
  }, [open, lesson]);

  const onSubmit = async (data: any) => {
    const lessonData = {
      ...data,
      lesson_date: format(data.lesson_date, 'yyyy-MM-dd'),
      // ✅ Converte para número no submit; campo vazio vira 0
      offering_pix: parseFloat(data.offering_pix) || 0,
      offering_cash: parseFloat(data.offering_cash) || 0,
    };

    const success = lesson
      ? await updateLesson(lesson.id, lessonData)
      : await createLesson(lessonData);

    if (success) {
      form.reset();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {lesson ? 'Editar Aula' : 'Nova Aula'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="class_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Turma *</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma turma" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {classes
                        .filter(cls => cls.status === 'active')
                        .map((cls) => (
                          <SelectItem key={cls.id} value={cls.id}>
                            {cls.name} ({cls.age_group})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="teacher_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Professor *</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um professor" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {teachers
                        .filter(teacher => teacher.status === 'active')
                        .map((teacher) => (
                          <SelectItem key={teacher.id} value={teacher.id}>
                            {teacher.name}
                            {teacher.specialization && ` (${teacher.specialization})`}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lesson_date"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Data da Aula *</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value ? (
                            format(field.value, "dd/MM/yyyy")
                          ) : (
                            <span>Selecione uma data</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) => {
                          const day = date.getDay();
                          return day !== 0;
                        }}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="topic"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tópico da Aula</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Parábola do Bom Samaritano" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="offering_pix"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Oferta (Pix)</FormLabel>
                  <FormControl>
                    <Input {...field} type="number" placeholder="0.00"  />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
             />
            <FormField
              control={form.control}
              name="offering_cash"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Oferta (Dinheiro)</FormLabel>
                  <FormControl>
                    <Input {...field} type="number" placeholder="0.00"  />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
           />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observações</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Observações sobre a aula"
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};